import { useQuery } from '@tanstack/react-query';
import { fetchAuthProviders, getOAuthStartUrl } from '../../lib/apiClient';
import { authKeys } from '../../lib/api/auth.gen';
import { CardActions, Cluster, Stack } from '../ui';

const PROVIDER_ICONS: Record<string, JSX.Element> = {
  google: (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.63h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.8Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.88-3.01c-1.07.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.72-4.95H1.27v3.11A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.28 14.28A7.21 7.21 0 0 1 4.9 12c0-.79.14-1.56.38-2.28V6.61H1.27a12 12 0 0 0 0 10.78l4.01-3.11Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.77c1.76 0 3.34.61 4.59 1.8l3.44-3.44A11.98 11.98 0 0 0 1.27 6.61l4.01 3.11C6.22 6.88 8.87 4.77 12 4.77Z"
      />
    </svg>
  ),
};

/**
 * External sign-in options, driven by the backend provider-strategy registry
 * (D1). New providers (Microsoft, Apple, enterprise SSO) appear here as soon
 * as their registry row is enabled — no frontend change needed.
 */
export function OAuthProviderButtons({
  intent,
  email,
}: {
  intent: 'login' | 'signup';
  // When set (login, after the email is entered), providers are resolved
  // against that user's org flags so a disabled method disappears (1F-2).
  email?: string | undefined;
}): JSX.Element | null {
  const { data: resolution } = useQuery({
    // Generated `auth.gen.ts` factory (G-19) — same shape `LoginPage` keys on
    // for its own copy of this resolution, so the two share one cache entry.
    queryKey: authKeys.list({ email: email ?? '' }),
    queryFn: () => fetchAuthProviders(email),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const oauthProviders = (resolution?.providers ?? []).filter((provider) => provider.kind === 'oauth');
  if (oauthProviders.length === 0) {
    return null;
  }

  return (
    <Stack gap={4}>
      <Cluster gap={3} role="separator" aria-label="or">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground">or</span>
        <span className="h-px flex-1 bg-border" />
      </Cluster>
      <Stack gap={2}>
        {/* Story 2-147xd: `CardActions` + `ActionDescriptor.block` (grown for
            exactly this shared "fill the containing block" need — see
            ActionHosts.tsx's own doc comment, which names this call site and
            `PaywallModal.tsx`'s plan CTA as the real 2-site repeat). */}
        {oauthProviders.map((provider) => (
          <CardActions
            key={provider.key}
            align="start"
            // `md`, not `CardActions`' own `sm` default — this button sits
            // directly beside the page's `FormSubmit` primary action (a bare
            // `Button` with no `size` prop, defaulting to `Button`'s own
            // `'md'`), and a `sm`/`md` mismatch between two stacked buttons
            // in one form is exactly the kind of visible height drift the
            // control-height convergence (story 2-147qa) exists to prevent.
            size="md"
            actions={[
              {
                id: provider.key,
                label: intent === 'signup' ? `Sign up with ${provider.label}` : `Continue with ${provider.label}`,
                variant: 'secondary',
                block: true,
                icon: PROVIDER_ICONS[provider.key] ?? undefined,
                onClick: () => window.location.assign(getOAuthStartUrl(provider.key)),
              },
            ]}
          />
        ))}
      </Stack>
    </Stack>
  );
}
